import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../types";
import { isDigitalDocument, isDocumentFlowCbe } from "../helpers/typeChecks";
import { shouldHideAccountingFlow as checkShouldHideAccountingFlow } from "../helpers/flowTypeUtils";

export type FlowType = "document" | "accounting";

interface UseFlowTypeProps {
  eventData?: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[];
  cbeData?: CanonicalBusinessEvent[];
  glProduct?: string;
}

interface UseFlowTypeResult {
  flowType: FlowType;
  setFlowType: (newFlowType: FlowType) => void;
  filteredEvents: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[];
  filteredCbes: CanonicalBusinessEvent[];
  showFlowTypeSelector: boolean;
  shouldHideAccountingFlow: boolean;
  documentEvents: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[];
  accountingEvents: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[];
  documentCbes: CanonicalBusinessEvent[];
  accountingCbes: CanonicalBusinessEvent[];
}

export const FLOW_PARAM = "flowType";

/**
 * Custom hook to manage flow type selection and filtering
 */
export function useFlowType({
  eventData = [],
  cbeData = [],
  glProduct = "",
}: UseFlowTypeProps): UseFlowTypeResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-filter events and CBEs by type
  const { documentEvents, accountingEvents, documentCbes, accountingCbes } =
    useMemo(() => {
      return {
        documentEvents: eventData.filter(isDigitalDocument),
        accountingEvents: eventData.filter(
          (event) => !isDigitalDocument(event)
        ),
        documentCbes: cbeData.filter(isDocumentFlowCbe),
        accountingCbes: cbeData.filter((cbe) => !isDocumentFlowCbe(cbe)),
      };
    }, [eventData, cbeData]);

  // Calculate derived state
  const { hasDocumentFlow, hasAccountingFlow, shouldHideAccountingFlow } =
    useMemo(() => {
      const documentFlowExists =
        documentEvents.length > 0 || documentCbes.length > 0;
      const accountingFlowExists =
        accountingEvents.length > 0 || accountingCbes.length > 0;
      const hideAccountingFlow = checkShouldHideAccountingFlow(
        eventData,
        glProduct
      );

      return {
        hasDocumentFlow: documentFlowExists,
        hasAccountingFlow: accountingFlowExists,
        shouldHideAccountingFlow: hideAccountingFlow,
      };
    }, [
      documentEvents,
      documentCbes,
      accountingEvents,
      accountingCbes,
      eventData,
      glProduct,
    ]);

  // Determine if we should show the flow selector
  const showFlowTypeSelector = useMemo(() => {
    return !shouldHideAccountingFlow && hasDocumentFlow && hasAccountingFlow;
  }, [shouldHideAccountingFlow, hasDocumentFlow, hasAccountingFlow]);

  // Determine default flow type based on available data
  const defaultFlowType = useMemo<FlowType>(() => {
    if (shouldHideAccountingFlow || (hasDocumentFlow && !hasAccountingFlow)) {
      return "document";
    }
    return "accounting"; // Default to accounting if both are available or only accounting exists
  }, [shouldHideAccountingFlow, hasDocumentFlow, hasAccountingFlow]);

  const urlFlowType = searchParams.get(FLOW_PARAM) as FlowType | null;

  // User's selected flow type (null means use default)
  const [selectedFlowType, setSelectedFlowType] = useState<FlowType | null>(
    urlFlowType
  );

  // Update URL when selected flow type changes
  useEffect(() => {
    if (selectedFlowType && showFlowTypeSelector) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(FLOW_PARAM, selectedFlowType);
          return newParams;
        },
        { replace: true } // dont add to browser history
      );
    }
  }, [selectedFlowType, showFlowTypeSelector, setSearchParams]);

  useEffect(() => {
    if (urlFlowType) {
      setSelectedFlowType(urlFlowType);
    }
  }, [urlFlowType]);

  // The actual flow type to use (selected or default)
  const flowType = selectedFlowType || defaultFlowType;

  // Filter events and CBEs based on the current flow type
  const filteredEvents = useMemo(() => {
    return flowType === "document" ? documentEvents : accountingEvents;
  }, [flowType, documentEvents, accountingEvents]);

  const filteredCbes = useMemo(() => {
    return flowType === "document" ? documentCbes : accountingCbes;
  }, [flowType, documentCbes, accountingCbes]);

  // Handler to change the flow type
  const setFlowType = useCallback((newFlowType: FlowType) => {
    setSelectedFlowType(newFlowType);
  }, []);

  return {
    flowType,
    setFlowType,
    filteredEvents,
    filteredCbes,
    showFlowTypeSelector,
    shouldHideAccountingFlow,
    documentEvents,
    accountingEvents,
    documentCbes,
    accountingCbes,
  };
}
